import { useState } from 'react';

import { IconClose, IconFlask, IconOtLamp, IconPulse } from '../../components/layout/icons';
import { Modal } from '../../components/ui/Modal';
import type { MrdDepartmentRow, MrdDetailNumberField, MrdDetailState } from './types';

type MrdNumberField = MrdDetailNumberField;
type MrdToggleField = 'pathologyEnabled' | 'radiologyEnabled' | 'otSectionEnabled';

const detailFromRow = (row: MrdDepartmentRow): MrdDetailState => ({
  avgOcc: row.avgOcc,
  pathologyEnabled: row.pathology.enabled,
  pathologyBiochemistry: row.pathology.biochemistry,
  pathologyHaematology: row.pathology.haematology,
  pathologyClinicalPathology: row.pathology.clinicalPathology,
  pathologyHistopathology: row.pathology.histopathology,
  pathologyCytology: row.pathology.cytology,
  pathologyCs: row.pathology.cs,
  pathologySerology: row.pathology.serology,
  radiologyEnabled: row.radiology.enabled,
  radiologyXray: row.radiology.xray,
  radiologyUsg: row.radiology.usg,
  radiologyCtRoutine: row.radiology.ctRoutine,
  radiologyMri: row.radiology.mri,
  otSectionEnabled: row.otSection.enabled,
  otMajor: row.otSection.major,
  otMinor: row.otSection.minor,
});

const pathologyFields: { key: MrdNumberField; label: string }[] = [
  { key: 'pathologyBiochemistry', label: 'Biochemistry' },
  { key: 'pathologyHaematology', label: 'Haematology' },
  { key: 'pathologyClinicalPathology', label: 'Clinical Pathology' },
  { key: 'pathologyHistopathology', label: 'Histopathology' },
  { key: 'pathologyCytology', label: 'Cytology' },
  { key: 'pathologyCs', label: 'C/S' },
  { key: 'pathologySerology', label: 'Serology' },
];

const radiologyFields: { key: MrdNumberField; label: string }[] = [
  { key: 'radiologyXray', label: 'X-Ray' },
  { key: 'radiologyUsg', label: 'Ultrasonography' },
  { key: 'radiologyCtRoutine', label: 'CT Routine' },
  { key: 'radiologyMri', label: 'MRI' },
];

const otSectionFields: { key: MrdNumberField; label: string }[] = [
  { key: 'otMajor', label: 'Major' },
  { key: 'otMinor', label: 'Minor' },
];

interface MrdEntryModalProps {
  row: MrdDepartmentRow;
  date: string;
  opdTotal: number;
  ipdTotal: number;
  emgTotal: number;
  otTotal: number;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (detail: MrdDetailState) => void;
}

export const MrdEntryModal = ({ row, date, opdTotal, ipdTotal, emgTotal, otTotal, submitting, onClose, onSubmit }: MrdEntryModalProps) => {
  const [detail, setDetail] = useState<MrdDetailState>(() => detailFromRow(row));

  const updateNumber = (field: MrdNumberField, value: string) => {
    const parsed = Math.floor(Number(value));
    setDetail((current) => ({ ...current, [field]: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0 }));
  };

  const toggleSection = (field: MrdToggleField) => {
    setDetail((current) => ({ ...current, [field]: !current[field] }));
  };

  const renderFieldGroup = (fields: { key: MrdNumberField; label: string }[], enabled: boolean) => (
    <div className="mrd-detail-grid">
      {fields.map((field) => (
        <label className="mrd-detail-field" key={field.key}>
          <span>{field.label}</span>
          <small>Count</small>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            disabled={!enabled}
            value={detail[field.key]}
            onChange={(event) => updateNumber(field.key, event.target.value)}
          />
        </label>
      ))}
    </div>
  );

  return (
    <Modal open onClose={onClose} labelledBy="mrd-modal-title" className="mrd-entry-modal">
      <div className="mrd-modal-header">
        <div>
          <h2 id="mrd-modal-title">MRD Entry</h2>
          <p>{row.departmentName} ({row.bedCount}) | {date} | Beds {row.occupiedBeds}/{row.maximumOccupiedBeds} at 80% limit</p>
        </div>
        <button type="button" className="mrd-modal-close" onClick={onClose} aria-label="Close"><IconClose /></button>
      </div>

      <div className="mrd-modal-body">
        <div className="mrd-summary-cards">
          <div className="mrd-summary-card"><span>OPD</span><strong>{opdTotal}</strong></div>
          <div className="mrd-summary-card"><span>IPD</span><strong>{ipdTotal}</strong></div>
          <div className="mrd-summary-card"><span>EMG</span><strong>{emgTotal}</strong></div>
          <div className="mrd-summary-card"><span>OT</span><strong>{otTotal}</strong></div>
          <div className="mrd-summary-card mrd-summary-card-editable">
            <span>Avg Occ</span>
            <input type="number" min={0} max={row.maximumOccupiedBeds} inputMode="numeric" value={detail.avgOcc} onChange={(event) => updateNumber('avgOcc', event.target.value)} />
          </div>
        </div>

        <section className="mrd-detail-section mrd-detail-pathology">
          <header className="mrd-detail-section-header">
            <span className="mrd-detail-section-title"><IconFlask />Pathology / PLET</span>
            <span className="mrd-detail-badges">
              <span className="mrd-badge">Lab</span>
              <button type="button" className={`mrd-toggle${detail.pathologyEnabled ? ' on' : ''}`} onClick={() => toggleSection('pathologyEnabled')}>
                {detail.pathologyEnabled ? 'ON' : 'OFF'}
              </button>
            </span>
          </header>
          {renderFieldGroup(pathologyFields, detail.pathologyEnabled)}
        </section>

        <section className="mrd-detail-section mrd-detail-radiology">
          <header className="mrd-detail-section-header">
            <span className="mrd-detail-section-title"><IconPulse />Radiology</span>
            <span className="mrd-detail-badges">
              <span className="mrd-badge">Imaging</span>
              <button type="button" className={`mrd-toggle${detail.radiologyEnabled ? ' on' : ''}`} onClick={() => toggleSection('radiologyEnabled')}>
                {detail.radiologyEnabled ? 'ON' : 'OFF'}
              </button>
            </span>
          </header>
          {renderFieldGroup(radiologyFields, detail.radiologyEnabled)}
        </section>

        <section className="mrd-detail-section mrd-detail-ot">
          <header className="mrd-detail-section-header">
            <span className="mrd-detail-section-title"><IconOtLamp />Operation Theatre — OT</span>
            <span className="mrd-detail-badges">
              <span className="mrd-badge">Surgery</span>
              <button type="button" className={`mrd-toggle${detail.otSectionEnabled ? ' on' : ''}`} onClick={() => toggleSection('otSectionEnabled')}>
                {detail.otSectionEnabled ? 'ON' : 'OFF'}
              </button>
            </span>
          </header>
          {renderFieldGroup(otSectionFields, detail.otSectionEnabled)}
          {detail.otSectionEnabled ? (
            <p className={`mrd-detail-total-hint${detail.otMajor + detail.otMinor === otTotal ? ' valid' : ' invalid'}`}>
              Major + Minor must equal the complete OT total: {otTotal}.
            </p>
          ) : null}
        </section>
      </div>

      <div className="mrd-modal-footer">
        <button type="button" className="button button-primary mrd-submit-button" disabled={submitting} onClick={() => onSubmit(detail)}>
          {submitting ? 'Saving…' : 'Submit Record'}
        </button>
      </div>
    </Modal>
  );
};
